import { isAxiosError } from 'axios'
import type { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form'
import { getApiErrorMessage } from './getApiErrorMessage'

export type ApplyApiValidationErrorsResult = {
  appliedFieldErrors: number
  formLevelMessage?: string
}

type ZodLikeIssue = {
  path: unknown[]
  message: string
}

function isZodLikeIssue(value: unknown): value is ZodLikeIssue {
  if (!value || typeof value !== 'object') {
    return false
  }
  const issue = value as { path?: unknown; message?: unknown }
  return Array.isArray(issue.path) && typeof issue.message === 'string'
}

function readZodLikeIssues(data: unknown): ZodLikeIssue[] {
  if (!data || typeof data !== 'object') {
    return []
  }
  const errors = (data as { errors?: unknown }).errors
  if (!Array.isArray(errors)) {
    return []
  }
  return errors.filter(isZodLikeIssue)
}

function readFieldNameFromIssuePath(path: unknown[]): string | undefined {
  const firstSegment = path[0]
  if (typeof firstSegment !== 'string' || firstSegment.length === 0) {
    return undefined
  }
  return firstSegment
}

export function applyApiValidationErrorsToForm<TFieldValues extends FieldValues>(
  form: UseFormReturn<TFieldValues>,
  error: unknown,
  allowedFields?: Set<string>
): ApplyApiValidationErrorsResult {
  if (!isAxiosError(error) || error.response?.status !== 400) {
    return {
      appliedFieldErrors: 0,
      formLevelMessage: getApiErrorMessage(error),
    }
  }

  const issues = readZodLikeIssues(error.response.data)
  let appliedFieldErrors = 0

  for (const issue of issues) {
    const fieldName = readFieldNameFromIssuePath(issue.path)
    if (!fieldName) {
      continue
    }
    if (allowedFields && !allowedFields.has(fieldName)) {
      continue
    }
    form.setError(fieldName as FieldPath<TFieldValues>, {
      type: 'server',
      message: issue.message,
    })
    appliedFieldErrors += 1
  }

  if (appliedFieldErrors > 0) {
    return { appliedFieldErrors }
  }

  return {
    appliedFieldErrors: 0,
    formLevelMessage: getApiErrorMessage(error),
  }
}
